import { useReducer } from 'react';
import { Assign, MoreOutline, Options } from '@doist/icons/product-ui';
import { useGSAP } from '@gsap/react';
import { ProductUIContextProvider, useProductUIContext } from 'components/product-ui/product-ui-context';
import { ProductUIFrame } from 'components/product-ui/product-ui-frame/product-ui-frame';
import { useProductUINSTranslation } from 'components/product-ui/product-ui-ns-context';
import { ProductUIViewHeaderCalendar } from 'components/product-ui/product-ui-view-header/product-ui-view-header-calendar';
import { cursorClick, moveElementTo } from 'components/product-ui/sequences/tweens';
import gsap from 'gsap';
import { ProductUIAppWindow } from '../../product-ui-app-window/product-ui-app-window';
import { ProductUIButton } from '../../product-ui-button/product-ui-button';
import { ProductUICursor } from '../../product-ui-cursor/product-ui-cursor';
import { ProductUIIconButton } from '../../product-ui-icon-button/product-ui-icon-button';
import { ProductUISceneContainer, ProductUISceneLayer } from '../../product-ui-scene-container/product-ui-scene-container';
import { TemplateSidebarStandard } from '../../product-ui-sidebar/product-ui-sidebar-templates';
import { ProductUISkeletonText } from '../../product-ui-skeleton-text/product-ui-skeleton-text';
import { ProductUIView } from '../../product-ui-view/product-ui-view';
import { BoardView } from './board-view';
import { CalendarView } from './calendar-view';
import { ContextMenu } from './context-menu';
import { ListView } from './list-view';
import css from './flexible-views-sequence.module.css';
export type ViewType = 'calendar' | 'list' | 'board';
type AnimationAction = {
  type: 'viewTypeChange';
  viewType: ViewType;
};
const animationState: {
  viewType: ViewType;
} = {
  viewType: 'calendar'
};
function animationReducer(state: typeof animationState, action: AnimationAction) {
  switch (action.type) {
    case 'viewTypeChange':
      return {
        ...state,
        ...action
      };
    default:
      return state;
  }
}
export function FlexibleViewsSequence() {
  return <ProductUIContextProvider data-sentry-element="ProductUIContextProvider" data-sentry-component="FlexibleViewsSequence" data-sentry-source-file="flexible-views-sequence.tsx">
            <ProductUIFrame data-sentry-element="ProductUIFrame" data-sentry-source-file="flexible-views-sequence.tsx">
                <FlexibleViews data-sentry-element="FlexibleViews" data-sentry-source-file="flexible-views-sequence.tsx" />
            </ProductUIFrame>
        </ProductUIContextProvider>;
}
function FlexibleViews() {
  const {
    t
  } = useProductUINSTranslation();
  const {
    timeline,
    scope
  } = useProductUIContext();
  const [state, dispatch] = useReducer(animationReducer, animationState);
  useGSAP(() => {
    if (!timeline) {
      return;
    }
    const cursorSelector = '[data-productui=cursor]';
    const calendarTabSelector = '[data-productui=tab-calendar]';
    const listTabSelector = '[data-productui=tab-list]';
    const boardTabSelector = '[data-productui=tab-board]';
    timeline.call(moveElementTo, [timeline, scope, cursorSelector, calendarTabSelector, {
      duration: 0
    }]).add(gsap.timeline().delay(0.5)).call(moveElementTo, [timeline, scope, cursorSelector, listTabSelector]).add(cursorClick(cursorSelector)).call(() => dispatch({
      type: 'viewTypeChange',
      viewType: 'list'
    })).add(gsap.timeline().delay(0.5)).call(moveElementTo, [timeline, scope, cursorSelector, boardTabSelector]).add(cursorClick(cursorSelector)).call(() => dispatch({
      type: 'viewTypeChange',
      viewType: 'board'
    })).add(gsap.timeline().delay(0.5)).call(moveElementTo, [timeline, scope, cursorSelector, calendarTabSelector]).add(cursorClick(cursorSelector)).call(() => dispatch({
      type: 'viewTypeChange',
      viewType: 'calendar'
    }));
  }, {
    dependencies: [timeline],
    scope
  });
  return <ProductUISceneContainer className={css.sequenceContainer} backgroundVariant="wave-three" data-sentry-element="ProductUISceneContainer" data-sentry-component="FlexibleViews" data-sentry-source-file="flexible-views-sequence.tsx">
            <ProductUISceneLayer position="bottomLeft" data-sentry-element="ProductUISceneLayer" data-sentry-source-file="flexible-views-sequence.tsx">
                <ProductUIAppWindow inlineSize={800} showSidebar sidebar={<TemplateSidebarStandard />} view={<ProductUIAppWindow.UIViews>
                            <ProductUIView variant={state.viewType}>
                                <ProductUIView.Header contextMenu={<ContextMenu selectedView={state.viewType} />} title={25} actions={<>
                                            <ProductUIButton variant="quinary" startIcon={<Assign />}>
                                                {t('share')}
                                            </ProductUIButton>
                                            <ProductUIButton hovered={true} variant="quinary" startIcon={<Options />}>
                                                {t('view.view')}
                                            </ProductUIButton>
                                            <ProductUIIconButton icon={<MoreOutline />} />
                                        </>}>
                                    {state.viewType === 'calendar' ? <ProductUIViewHeaderCalendar.Subtitle>
                                            <ProductUISkeletonText width="20" />
                                        </ProductUIViewHeaderCalendar.Subtitle> : null}
                                </ProductUIView.Header>
                                <div className={css.views}>
                                    {state.viewType === 'list' ? <ListView /> : null}
                                    {state.viewType === 'board' ? <BoardView /> : null}
                                    {state.viewType === 'calendar' ? <CalendarView /> : null}
                                </div>
                            </ProductUIView>
                        </ProductUIAppWindow.UIViews>} data-sentry-element="ProductUIAppWindow" data-sentry-source-file="flexible-views-sequence.tsx" />
            </ProductUISceneLayer>
            <ProductUICursor id="productUICursor" data-productui="cursor" data-sentry-element="ProductUICursor" data-sentry-source-file="flexible-views-sequence.tsx" />
        </ProductUISceneContainer>;
}